import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../config/api';

const CONFIG_RETRY_MS = 2000;
const CONFIG_MAX_RETRIES = 8;
const VERSION_POLL_MS = 30 * 1000;
const UPDATED_KEY = 'aflo-file-updated';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// ?mode=view → read-only editor. ?embedded=1 → we are inside the board's
// preview iframe (no Read-only badge, and nothing to window.close()).
function readQuery() {
  const q = new URLSearchParams(window.location.search);
  return { mode: q.get('mode') === 'view' ? 'view' : 'edit', embedded: q.get('embedded') === '1' };
}

// Tell the board tab this file may have new bytes. It listens for the storage
// event (fires in OTHER tabs only) and drops its files cache.
function notifyOpener(fileId) {
  try { localStorage.setItem(UPDATED_KEY, JSON.stringify({ fileId, ts: Date.now() })); } catch { /* storage blocked */ }
}

// Full-screen OnlyOffice editor for ONE attachment. Opened in its own tab from
// the file cell's Edit button (Word / Excel / PowerPoint). The API hands us a
// signed config plus the Document Server URL; we load DS's api.js and mount
// DocsAPI.DocEditor. Saving is automatic — DS calls our save-back endpoint on
// Ctrl+S / autosave / close — so there is no Save button to wire up.
export default function FileEditorPage() {
  const { fileId } = useParams();
  const { embedded } = readQuery();
  const [status, setStatus] = useState({ loading: true, waiting: false, error: null, mode: null, title: '' });
  const [conflict, setConflict] = useState(false);
  const editorRef = useRef(null);
  const baseTitleRef = useRef('');
  const dirtyRef = useRef(false);
  const lastPingRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    let poll = null;
    let openedMode = null;
    const { mode: requestedMode, embedded: isEmbedded } = readQuery();

    // Closing the tab is what triggers DS's final save-back, so the board tab
    // needs a nudge even when every edit was already flushed.
    const onUnload = () => { if (openedMode === 'edit') notifyOpener(fileId); };
    window.addEventListener('beforeunload', onUnload);
    window.addEventListener('pagehide', onUnload);

    // 409 = the previous session's save-back is still being written; wait for
    // it rather than opening stale bytes. Gives up with the server's reason.
    const fetchConfig = async (mode) => {
      for (let attempt = 0; ; attempt++) {
        try {
          return (await api.get(`/files/${fileId}/editor-config?mode=${mode}`)).data;
        } catch (e) {
          if (cancelled || e?.response?.status !== 409 || attempt >= CONFIG_MAX_RETRIES) throw e;
          setStatus((s) => ({ ...s, waiting: true }));
          await sleep(CONFIG_RETRY_MS);
          if (cancelled) throw e;
        }
      }
    };

    (async () => {
      try {
        let data;
        try {
          data = await fetchConfig(requestedMode);
        } catch (e) {
          // Not allowed to edit (board viewer) → same document, read-only.
          if (cancelled || requestedMode !== 'edit' || e?.response?.status !== 403) throw e;
          data = await fetchConfig('view');
        }
        if (cancelled) return;
        await new Promise((resolve, reject) => {
          if (window.DocsAPI) return resolve();
          const s = document.createElement('script');
          s.src = `${data.dsUrl}/web-apps/apps/api/documents/api.js`;
          s.async = true;
          s.onload = resolve;
          s.onerror = () => reject(new Error('Could not load the document editor'));
          document.head.appendChild(s);
        });
        if (cancelled) return;
        const title = data.config.document.title;
        baseTitleRef.current = `${title} — AcuityFlo`;
        document.title = baseTitleRef.current;
        openedMode = data.mode;
        editorRef.current = new window.DocsAPI.DocEditor('oo-editor', {
          ...data.config,
          width: '100%',
          height: '100%',
          events: {
            onError: (e) => setStatus((s) => ({ ...s, error: e?.data?.errorDescription || 'The editor reported an error' })),
            onRequestClose: () => { if (!isEmbedded) window.close(); },
            // data === true → unsaved edits; false → DS has flushed them
            // (autosave / Ctrl+S). Mirror it in the tab title like desktop apps do.
            onDocumentStateChange: (ev) => {
              const unsaved = ev?.data === true;
              document.title = unsaved ? `• ${baseTitleRef.current}` : baseTitleRef.current;
              if (!unsaved && dirtyRef.current) notifyOpener(fileId);
              dirtyRef.current = unsaved;
              // Every keystroke lands inside DS's cross-origin iframe, so the
              // app's idle timeout never sees it — count an edit as activity or
              // an active editor gets logged out after 30 minutes. This tab via
              // a synthetic event; the board tab (whose logout would clear the
              // shared token) via a storage ping, throttled to one per 10 s.
              try {
                window.dispatchEvent(new Event('mousemove'));
                const now = Date.now();
                if (now - lastPingRef.current > 10000) {
                  lastPingRef.current = now;
                  localStorage.setItem('aflo-activity', String(now));
                }
              } catch { /* ignore */ }
            },
          },
        });
        setStatus({ loading: false, waiting: false, error: null, mode: data.mode, title });

        // Someone else saving the same file bumps its version; warn before the
        // next save here silently forks into a conflict copy.
        if (data.mode === 'edit' && typeof data.version === 'number') {
          const opened = data.version;
          poll = setInterval(async () => {
            try {
              const { data: meta } = await api.get(`/files/${fileId}/meta`);
              if (cancelled || typeof meta?.version !== 'number' || meta.version === opened) return;
              setConflict(true);
              clearInterval(poll);
            } catch { /* transient — try again next tick */ }
          }, VERSION_POLL_MS);
        }
      } catch (e) {
        if (cancelled) return;
        setStatus({
          loading: false, waiting: false, mode: null, title: '',
          error: e?.response?.data?.detail || e?.message || 'Could not open the editor',
        });
      }
    })();
    return () => {
      cancelled = true;
      if (poll) clearInterval(poll);
      window.removeEventListener('beforeunload', onUnload);
      window.removeEventListener('pagehide', onUnload);
      try { editorRef.current?.destroyEditor?.(); } catch { /* already gone */ }
    };
  }, [fileId]);

  return (
    <div className="fixed inset-0 bg-gray-100 flex flex-col">
      {conflict && (
        <div className="flex-shrink-0 flex items-center gap-3 px-4 py-1.5 bg-amber-100 border-b border-amber-200 text-amber-900 text-xs">
          <span className="flex-1">
            This document was saved from another session. Reload to keep editing — unsaved changes here will be kept as a conflict copy.
          </span>
          <button
            onClick={() => window.location.reload()}
            className="px-3 py-1 rounded-md bg-amber-600 hover:bg-amber-700 text-white font-medium transition-colors flex-shrink-0"
          >
            Reload
          </button>
        </div>
      )}
      <div className="relative flex-1 min-h-0">
        <div id="oo-editor" className="w-full h-full" />
        {(status.loading || status.error) && (
          <div className="absolute inset-0 flex items-center justify-center bg-white">
            {status.error ? (
              <div className="text-center px-6 max-w-md">
                <p className="text-gray-800 font-medium mb-2">Can’t open the editor</p>
                <p className="text-sm text-gray-500 mb-6">{status.error}</p>
                {!embedded && (
                  <button
                    onClick={() => window.close()}
                    className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Close
                  </button>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-400">{status.waiting ? 'Waiting for a save to finish…' : 'Opening document…'}</p>
            )}
          </div>
        )}
        {!status.loading && !status.error && status.mode === 'view' && !embedded && (
          <div className="absolute top-2 right-2 text-[11px] px-2 py-1 rounded bg-amber-100 text-amber-800 shadow">
            Read-only
          </div>
        )}
      </div>
    </div>
  );
}
